import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Plus, Trash2, Edit2, Save, X, ExternalLink, Eye, MapPin, Loader2, AlertCircle, CheckCircle } from 'lucide-react';

const TOUR_CATEGORIES = [
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'education', label: 'Éducation' },
  { value: 'sante', label: 'Santé' },
  { value: 'commerce', label: 'Commerce' },
  { value: 'loisirs', label: 'Loisirs & Culture' },
  { value: 'transport', label: 'Transport' },
  { value: 'residentiel', label: 'Résidentiel' },
  { value: 'autre', label: 'Autre' },
];

const EMPTY_TOUR = {
  id: '',
  title: '',
  description: '',
  matterport_url: '',
  thumbnail_url: '',
  category: 'infrastructure',
};

const MatterportToursAdmin = () => {
  const [communes, setCommunes] = useState([]);
  const [selectedCommuneId, setSelectedCommuneId] = useState('');
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [editingTour, setEditingTour] = useState(null); // null = not editing, {} = new, {id} = existing
  const [formData, setFormData] = useState(EMPTY_TOUR);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Load communes list
  useEffect(() => {
    const fetchCommunes = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          ?.from('communes')
          ?.select('id, name, matterport_tours')
          ?.order('name', { ascending: true });
        if (!error) setCommunes(data || []);
      } catch (e) {
        console.error('Error fetching communes:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchCommunes();
  }, []);

  // When commune selected, load its tours
  useEffect(() => {
    if (!selectedCommuneId) {
      setTours([]);
      return;
    }
    const commune = communes?.find(c => c?.id === selectedCommuneId);
    const existing = commune?.matterport_tours;
    if (Array.isArray(existing)) {
      setTours(existing);
    } else if (typeof existing === 'string') {
      try { setTours(JSON.parse(existing)); } catch { setTours([]); }
    } else {
      setTours([]);
    }
    setEditingTour(null);
    setPreviewUrl(null);
  }, [selectedCommuneId, communes]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const saveTours = async (updatedTours) => {
    if (!selectedCommuneId) return;
    setSaving(true);
    try {
      const { error } = await supabase
        ?.from('communes')
        ?.update({ matterport_tours: updatedTours })
        ?.eq('id', selectedCommuneId);
      if (error) throw error;
      setTours(updatedTours);
      // Update local communes list
      setCommunes(prev => prev?.map(c =>
        c?.id === selectedCommuneId ? { ...c, matterport_tours: updatedTours } : c
      ));
      showToast('Visites immersives sauvegardées avec succès');
    } catch (e) {
      showToast('Erreur lors de la sauvegarde: ' + (e?.message || 'Erreur inconnue'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNew = () => {
    setEditingTour('new');
    setFormData({ ...EMPTY_TOUR, id: crypto.randomUUID() });
    setPreviewUrl(null);
  };

  const handleEdit = (tour) => {
    setEditingTour(tour?.id);
    setFormData({ ...tour });
    setPreviewUrl(null);
  };

  const handleCancelEdit = () => {
    setEditingTour(null);
    setFormData(EMPTY_TOUR);
    setPreviewUrl(null);
  };

  const handleSaveTour = async () => {
    if (!formData?.title?.trim() || !formData?.matterport_url?.trim()) {
      showToast('Le titre et le lien Matterport sont obligatoires', 'error');
      return;
    }
    // Validate Matterport URL
    const url = formData?.matterport_url?.trim();
    if (!url?.includes('matterport.com') && !url?.includes('my.matterport') && !url?.startsWith('https://')) {
      showToast('Veuillez entrer un lien Matterport valide (ex: https://my.matterport.com/show/?m=...)', 'error');
      return;
    }

    let updatedTours;
    if (editingTour === 'new') {
      updatedTours = [...tours, { ...formData }];
    } else {
      updatedTours = tours?.map(t => t?.id === editingTour ? { ...formData } : t);
    }
    await saveTours(updatedTours);
    setEditingTour(null);
    setFormData(EMPTY_TOUR);
  };

  const handleDelete = async (tourId) => {
    if (!window.confirm('Supprimer cette visite immersive ?')) return;
    let updatedTours = tours?.filter(t => t?.id !== tourId);
    await saveTours(updatedTours);
  };

  const getMatterportEmbedUrl = (url) => {
    if (!url) return null;
    try {
      // Handle various Matterport URL formats
      if (url?.includes('/show/') || url?.includes('?m=')) {
        // Already a valid embed URL or show URL
        const urlObj = new URL(url);
        const modelId = urlObj?.searchParams?.get('m') || urlObj?.pathname?.split('/')?.pop();
        if (modelId) return `https://my.matterport.com/show/?m=${modelId}&play=1`;
      }
      return url;
    } catch {
      return url;
    }
  };

  const selectedCommune = communes?.find(c => c?.id === selectedCommuneId);

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-white text-sm font-medium transition-all ${
          toast?.type === 'error' ? 'bg-red-600' : 'bg-green-600'
        }`}>
          {toast?.type === 'error' ? <AlertCircle className="w-4 h-4 flex-shrink-0" /> : <CheckCircle className="w-4 h-4 flex-shrink-0" />}
          {toast?.message}
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Visites Immersives Matterport</h2>
          <p className="text-sm text-gray-500 mt-1">Gérez les visites 3D par commune — ajoutez des liens Matterport pour chaque infrastructure</p>
        </div>
      </div>
      {/* Commune Selector */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          <MapPin className="inline w-4 h-4 mr-1 text-blue-600" />
          Sélectionner une Commune
        </label>
        {loading ? (
          <div className="flex items-center gap-2 text-gray-400 text-sm py-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement des communes...
          </div>
        ) : (
          <select
            value={selectedCommuneId}
            onChange={e => setSelectedCommuneId(e?.target?.value)}
            className="w-full max-w-sm border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">-- Choisir une commune --</option>
            {communes?.map(c => (
              <option key={c?.id} value={c?.id}>
                {c?.name} {Array.isArray(c?.matterport_tours) && c?.matterport_tours?.length > 0 ? `(${c?.matterport_tours?.length} visite${c?.matterport_tours?.length > 1 ? 's' : ''})` : ''}
              </option>
            ))}
          </select>
        )}
      </div>
      {/* Tours Management */}
      {selectedCommuneId && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Panel Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50">
            <div>
              <h3 className="font-semibold text-gray-900">{selectedCommune?.name}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{tours?.length} visite{tours?.length !== 1 ? 's' : ''} immersive{tours?.length !== 1 ? 's' : ''} configurée{tours?.length !== 1 ? 's' : ''}</p>
            </div>
            <button
              onClick={handleAddNew}
              disabled={editingTour !== null}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Plus className="w-4 h-4" />
              Ajouter une visite
            </button>
          </div>

          {/* Add/Edit Form */}
          {editingTour !== null && (
            <div className="p-5 border-b border-gray-100 bg-blue-50">
              <h4 className="font-semibold text-gray-800 mb-4 text-sm">
                {editingTour === 'new' ? '➕ Nouvelle visite immersive' : '✏️ Modifier la visite'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Titre *</label>
                  <input
                    type="text"
                    value={formData?.title}
                    onChange={e => setFormData(p => ({ ...p, title: e?.target?.value }))}
                    placeholder="Ex: Mairie de Cocody"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Catégorie</label>
                  <select
                    value={formData?.category}
                    onChange={e => setFormData(p => ({ ...p, category: e?.target?.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {TOUR_CATEGORIES?.map(cat => (
                      <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Lien Matterport *</label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={formData?.matterport_url}
                      onChange={e => setFormData(p => ({ ...p, matterport_url: e?.target?.value }))}
                      placeholder="https://my.matterport.com/show/?m=XXXXXXXXXX"
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {formData?.matterport_url && (
                      <button
                        onClick={() => setPreviewUrl(getMatterportEmbedUrl(formData?.matterport_url))}
                        className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-medium transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" /> Prévisualiser
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Format: https://my.matterport.com/show/?m=IDENTIFIANT</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">URL Miniature (optionnel)</label>
                  <input
                    type="url"
                    value={formData?.thumbnail_url}
                    onChange={e => setFormData(p => ({ ...p, thumbnail_url: e?.target?.value }))}
                    placeholder="https://..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Description (optionnel)</label>
                  <input
                    type="text"
                    value={formData?.description}
                    onChange={e => setFormData(p => ({ ...p, description: e?.target?.value }))}
                    placeholder="Brève description de l'infrastructure"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Preview iframe */}
              {previewUrl && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-gray-600">Prévisualisation Matterport</p>
                    <button onClick={() => setPreviewUrl(null)} className="text-gray-400 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="relative w-full rounded-lg overflow-hidden border border-gray-200" style={{ paddingTop: '56.25%' }}>
                    <iframe
                      src={previewUrl}
                      className="absolute inset-0 w-full h-full"
                      allowFullScreen
                      allow="xr-spatial-tracking"
                      title="Matterport Preview"
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 mt-4">
                <button
                  onClick={handleSaveTour}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  <X className="w-4 h-4" /> Annuler
                </button>
              </div>
            </div>
          )}

          {/* Tours List */}
          <div className="divide-y divide-gray-100">
            {tours?.length === 0 ? (
              <div className="py-12 text-center text-gray-400">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Eye className="w-6 h-6 text-gray-300" />
                </div>
                <p className="text-sm font-medium text-gray-500">Aucune visite immersive configurée</p>
                <p className="text-xs text-gray-400 mt-1">Cliquez sur "Ajouter une visite" pour commencer</p>
              </div>
            ) : (
              tours?.map((tour, index) => {
                const category = TOUR_CATEGORIES?.find(c => c?.value === tour?.category);
                return (
                  <div key={tour?.id || index} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors">
                    {/* Thumbnail */}
                    <div className="w-16 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-200">
                      {tour?.thumbnail_url ? (
                        <img src={tour?.thumbnail_url} alt={tour?.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Eye className="w-5 h-5 text-gray-300" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-semibold text-gray-900 text-sm truncate">{tour?.title}</p>
                        {category && (
                          <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                            {category?.label}
                          </span>
                        )}
                      </div>
                      {tour?.description && (
                        <p className="text-xs text-gray-500 truncate">{tour?.description}</p>
                      )}
                      <p className="text-xs text-gray-400 truncate mt-0.5">{tour?.matterport_url}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <a
                        href={tour?.matterport_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Ouvrir dans Matterport"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => handleEdit(tour)}
                        disabled={editingTour !== null}
                        className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-40"
                        title="Modifier"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(tour?.id)}
                        disabled={saving || editingTour !== null}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MatterportToursAdmin;
